import { InfiniteData } from 'react-query';

export interface IPaginationResultRequests<T> {
  totalPages: number;
  totalAssets: number;
  assetsPerPage: number;
  currentPage: number;
  isLastPage: boolean;
  assets: T[];
}

export type IdentificationRequestStatus =
  | 'open'
  | 'information_needed'
  | 'confirm_product'
  | 'closed'
  | 'resolved';

export type IdentificationRequestAsset = {
  mpn: string;
  mfr: string;
  productName: string;
};

export type IdentificationRequestAssigned = {
  avatar: string;
  email: string;
  id: string;
  name: string;
};

export interface IIdentificationRequest {
  assigned: IdentificationRequestAssigned;
  asset: IdentificationRequestAsset;
  id: string;
  studioId: string;
  fileId: string;
  assetId: string;
  groupId: string;
  idenficationError: string;
  source: string;
  studioName: string;
  chats: number;
  status: IdentificationRequestStatus;
  priority: string;
  createdAt: Date;
  updatedAt: Date;
  threadId: string;
}

export interface IIdentificationRequestApi {
  assigned: IdentificationRequestAssigned;
  asset: IdentificationRequestAsset;
  _id: string;
  studioId: string;
  fileId: string;
  assetId: string;
  groupId: string;
  idenficationError: string;
  source: string;
  studioName: string;
  chats: number;
  status: IdentificationRequestStatus;
  priority: string;
  createdAt: string;
  updatedAt: string;
  threadId: string;
  __v: number;
}

export interface IIdentificationRequestsRaw {
  filesQuery?: InfiniteData<IPaginationResultRequests<IIdentificationRequest>>;
  fetchNextPage: () => void;
  isError: boolean;
  isLoading: boolean;
}

export default class IdentificationRequest implements IIdentificationRequest {
  public readonly assigned: IdentificationRequestAssigned;

  public readonly asset: IdentificationRequestAsset;

  public readonly id: string;

  public readonly studioId: string;

  public readonly fileId: string;

  public readonly assetId: string;

  public readonly groupId: string;

  public readonly idenficationError: string;

  public readonly source: string;

  public readonly studioName: string;

  public readonly chats: number;

  public readonly status: IdentificationRequestStatus;

  public readonly priority: string;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly threadId: string;

  constructor(
    id: string,
    assigned: IdentificationRequestAssigned,
    asset: IdentificationRequestAsset,
    studioId: string,
    fileId: string,
    assetId: string,
    groupId: string,
    idenficationError: string,
    source: string,
    studioName: string,
    chats: number,
    status: IdentificationRequestStatus,
    priority: string,
    createdAt: Date,
    updatedAt: Date,
    threadId: string,
  ) {
    this.id = id;
    this.assigned = assigned;
    this.asset = asset;
    this.studioId = studioId;
    this.fileId = fileId;
    this.assetId = assetId;
    this.groupId = groupId;
    this.idenficationError = idenficationError;
    this.source = source;
    this.studioName = studioName;
    this.status = status;
    this.chats = chats;
    this.priority = priority;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.threadId = threadId;
  }

  static fromApiModel(apiModel: IIdentificationRequestApi) {
    const {
      _id,
      asset,
      assetId,
      assigned,
      chats,
      fileId,
      priority,
      status,
      studioId,
      threadId,
      groupId,
      idenficationError,
      source,
      studioName,
    } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));

    return new IdentificationRequest(
      _id,
      assigned,
      asset,
      studioId,
      fileId,
      assetId,
      groupId,
      idenficationError,
      source,
      studioName,
      chats,
      status,
      priority,
      createdAt,
      updatedAt,
      threadId,
    );
  }

  static arrayFromApiModel(arrayApiModel: IIdentificationRequestApi[]) {
    const array = arrayApiModel.map(apiModel => this.fromApiModel(apiModel));

    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResultRequests<IIdentificationRequestApi>,
  ) {
    const array = this.arrayFromApiModel(arrayApiModel.assets);

    return {
      ...arrayApiModel,
      assets: array,
    };
  }
}
